<template>
    <div>
        <h1 style="color:cornflowerblue">Roll20 Map Generator:</h1>        
        <h2>What It Does:</h2>
        <p>The Akashic Paths Map Generator is an API script that can be added to any roll20 account with a “Pro” subscription that can be run to automatically generate a map from a list of prebuilt maps for usage during an Akashic Paths Game. Each map has everything you need to play GMing a game of the Akashic Paths easier than ever before. Each map includes battle maps, tokens for the monsters and treasure, information needed to run each map, dynamic lighting walls, and everything else you could need. Additionally, these tokens are added to your map without adding the art to your library, so it wont clutter up your own personal library or use its storage.</p>
        <br/><br/>
        <h2>Getting the Map Builder:</h2>
        <p>There are two map generator scripts: 
            <br/><b>Map Pack 1</b> contains the playtest maps and you can get it <a href="https://github.com/Threeden/Akashic-Paths-Map-Creator/blob/playtest-maps/mapCreator.js" target="_blank">here</a>
            <br/><b>Map Pack 2</b> contains an expanded map list to add to your games, as well as a player art selection, and you get get it <a href="https://github.com/Threeden/mapGen2/blob/main/mapgen2.js" target="_blank">here</a>  
            <br/>Copy and add them to your game as shown below:</p>
            <br/>Additional Maps: 
            <br/><a href="https://files.d20.io/images/229111405/1pjwJXCu_i1FqOqYCLrKuA/original.jpg?162397111755">Store Map</a>
            <br/><a href="https://files.d20.io/images/241968041/l3LvhXSnC7_qNdpsBORf4w/original.jpg?16300335535555">Hall of Fame Map</a>
            <br/><a href="https://files.d20.io/images/344392540/WWSrMcQnWy3bmSZioeD0Pw/original.webp?168566998655">Hall of Shame Map</a> 
        <br/><br/>
        <v-row>
            <v-col sm="6" style="align-self:center"
            >Then go to the roll20 game you are using for your Akashic Paths game, and go to its settings, and click the “Mod (API) Scripts” button as shown:
            </v-col>
            <v-col sm="6">
                <v-img :src="require('../../assets/Mod(API) scripts location.jpg')"  contain height="360px"/>
            </v-col>
        </v-row>
        <br/><br/>
        <v-row>
            <v-col sm="6" style="align-self:center"
            >After that you click “New Script” as shown:   
            </v-col>
            <v-col sm="6">
                <v-img :src="require('../../assets/new script.jpg')"  contain height="360px"/>
            </v-col>
        </v-row>
        <br/><br/>
        <v-row>
            <v-col sm="6" style="align-self:center"
            >Finally paste the copied code into the box shown in arrow 1, and click save the script button shown in arrow 2:
            </v-col>
            <v-col sm="6">
                <v-img :src="require('../../assets/saving script.jpg')"  contain height="360px"/>
            </v-col>
        </v-row>
        <br/>
        This script is somewhat large, so it’s recommended that you disable the script after you use it to generate your maps.
        <br/><br/>
        <h2>Usage Information:</h2>
        <p>To create a map you need to first set the map you want to modify to be the player map, then use the command “!create maps [Map Name]”, replacing [Map Name] with the name of the map. The list of currently available maps is listed below. You can also use commands to get additional information by typing “!create maps Help”. The commands and map names are case and grammar sensitive. The generator will create the map on top of whatever map is selected, so if you want to clear and recreate a map make sure to first delete everything on the map and then generate it. </p>
        <br/><br/>
        <h2>About the Maps Being Created:</h2>
        <p>The maps created should have all the information you need to run them once the generator has been run, to make running an Akashic Paths game as easy as possible. Each map comes complete with: 
            <ul>
                <li>Battle map, including any map dressing also used, and dynamic lighting walls have been drawn if needed for that map.</li>
                <li>Monsters have been placed in their spawn locations on the map, or if its an arena map often have default wave orders. Many maps also have additional monsters that can easily be swapped out for current ones to keep encounters interesting. Any changes to a monster are listed in their GM notes.</li>
                <li>Treasure has been placed on the map, and the skill checks required to obtain them are listed in its bars. If an items check says CR+X, it means the DC of the check is 20 + the map's challenge rating + X.</li>
                <li>Book tokens have been placed throughout the maps with details about areas of interest, or map features.</li>
                <li>Monster tokens are on the right side of the map with their name and CR beneath them so you can find their stat cards easily. Additional map thematic monsters are often included.</li>
                <li>If a map includes custom creature stats there will be a link to a google doc containing their stats.</li>
            </ul>
            <br/>
            Although these maps are ready to use as soon as you run the generator, they are still easily modifiable and come with many resources to do so. Many Arena maps have a large set of additional monsters that can be swapped in, and treasure types and locations can be quickly swapped to keep each encounter interesting. Arena maps are especially modifiable, and many can be run at multiple CR’s and on multiple floors and have all the monster’s you’d need to run them on the right side. You can use a simple CR calculator to determine how many monsters of what CR to include per encounter. Some maps even if they can be run at various CR’s still have a preference, so make sure to check out any details about the map in the map list.
        </p>
        <h3>Maps</h3>
        <p>The art used in the Akashic Paths map generator is pulled directly from my roll20 library and is the art I use in my games, as such not all of it is going to be perfectly pathfinder lore accurate, and plenty of it is just art I thought looked cool. If you have any issues with any of your art being linked in this script feel free to contact me at akashicpaths@gmail.com.
        </p>
        <h2>Playtest</h2>
        <p>The playtest is made to be as easily accessible as possible, because of this maps created from the playtest will have links to their stat sheets listed in their GM notes, their health points in their red bars, their AC in their green bars, with the format AC/Touch AC, and if there is additional notes about the creature it will say “notes” in their blue bar, check the GM notes on the token to see more. To avoid over cluttering the maps with books some of the less important details of the maps have been left out, so if you need additional information make sure to check the playtest notes. I will attempting to add this to all maps, but that will take a while.</p>
        <br/><br/>
        <h2>Journey Select Generator</h2>
        <p>You can use this to create a journey selection, though hand crafting them does often come out with better options</p>
        <div style="display:flex">
            <v-combobox
                v-model="floorCount"
                :items="floors"
                label="Floor Count"            
                outlined
                dense
                class="ma-2"
                style="max-width:150px"
            ></v-combobox>
            <v-combobox
                v-model="combatCount"
                :items="combats"
                label="Combat Count"            
                outlined
                dense
                class="ma-2"
                style="max-width:150px;"
            ></v-combobox>
            <v-btn @click="createJourneySelect()" class="ma-2">generate</v-btn>
        </div>
        <v-row v-for="(floor, x) in journeySelect" :key="x" style="justify-content:center">
            <v-col cols="3" v-for="(encounter, y) in floor" :key="y" >
                <v-card style="min-height:60px; text-align:center;align-content:center" class="ma-1">
                    <span v-if="encounter.name">
                    <b>{{encounter.name}}</b><br/>
                    CR: {{encounter.specificCR}}<br/>
                    Rewards: {{encounter.rewards}}<br/>
                    </span>
                    <span v-else>
                        {{encounter}}
                    </span>
                </v-card>
            </v-col>
        </v-row>
        <br/><br/>
        <h2>Maplist:</h2>
        <table style="width:100%">
            <tr> 
                <th>Name</th>
                <th>Map Pack</th>
                <th>Type</th>
                <th>CR</th>
                <th>Special</th>
                <th>File Link</th>
            </tr>
            <tr v-for="map in mapList" :key="map.name">
                <td style="text-align:center">{{map.name}}</td>
                <td style="text-align:center">{{map.mapPack}}</td>
                <td style="text-align:center">{{map.type}}</td>
                <td style="text-align:center">{{map.cr}}</td>
                <td style="text-align:center">{{map.special}}</td>  
                <td style="text-align:center"><a v-if="map.link" :href="map.link">Link</a></td>
            </tr>
        </table>  
        <br/>        
    </div>
</template>
<script>
import {
VImg,
VRow,
VCol,
VCombobox,
VBtn,
VCard,
} from "vuetify/lib";
export default {
    components: {
        VImg,    
        VRow,
        VCol,
        VCombobox,
        VBtn,
        VCard,
    },
    data() {
          return {
            floorCount:6,
            combatCount:4,
            combats:[3,4],
            floors:[2,3,4,6],
            journeySelect:[],
            floorCRs:[[3,4,5],[7,8,9,10],[12,13,14,15],[17,18,19,20],[22,23,24,25],[30]],
            averageCR:[[4],[8,9],[13,14],[18,19],[23,24]],
            bossCR:[9,14,19,24,30],
            floorGold:[250,250,300,350,400],
            storeTypes:['Destruction', 'Protection', 'Armory', 'Training Hall', 'Hall of Ascension', 'Inn', 'Bargain', 'Magic'],
            mapsSelected:[],
              mapList: [
              {name:"Desert Tomb", type:"Long", cr:"3", mapPack:"1"},
              {name:"Mage's Dungeon", type:"Long", cr:"4", mapPack:"1"},
              {name:"Drake's Lair", type:"Long", cr:"5", mapPack:"1"},
              {name:"Plane of Fire", type:"Arena", cr:"7-12", special:"High Loot", mapPack:"1"},
              {name:"Earth Shrine", type:"Arena", cr:"7-8", mapPack:"1"},
              {name:"Desert Ruin", type:"Arena", cr:"7-10", mapPack:"1"},
              {name:"Pharaoh's Tomb", type:"Split Arena", cr:"12", mapPack:"1"},
              {name:"Throne of Heaven", type:"Split Arena", cr:"12-15", mapPack:"1"},
              {name:"Fey Court", type:"Split Arena", cr:"12-15", mapPack:"1"},
              {name:"Ancient Prison", type:"Boss Arena", cr:"18", mapPack:"1"},
              {name:"Astral Realm", type:"Boss Arena", cr:"18", mapPack:"1"},
              {name:"Beast's Lair", type:"Boss Arena", cr:"18", mapPack:"1"},
              {name:"Caves of Chaos", type:"Long", cr:"4", mapPack:"2", link:"https://docs.google.com/document/d/1zwJPQGSmHgeRXjeovRgQL5kzCrsOz9MYVyb014-DQXo/edit?usp=sharing"},
              {name:"Temple of Knowledge", type:"Long", cr:"5", mapPack:"2"},
              {name:"Temple of Sand", type:"Long", cr:"5", mapPack:"2", link:"https://docs.google.com/document/d/1wLwYEAbQlwRJ-NAcNQHLiufjzqCwubZfdd_U_9uJ_L8/edit?usp=sharing"},
              {name:"Balance", type:"Arena", cr:"7-22", mapPack:"2"},
              {name:"Forest Temple", type:"Arena", cr:"7-12", mapPack:"2"},
              {name:"Deep Ocean", type:"Arena", cr:"7-15", mapPack:"2"},
              {name:"Frostbite", type:"Arena", cr:"7-13", mapPack:"2"},
              {name:"Hades", type:"Arena", cr:"9-22", mapPack:"2"},
              {name:"Giant's Arena", type:"Arena", cr:"9-22", mapPack:"2"},
              {name:"Air", type:"Arena", cr:"7-19", mapPack:"2"},
              {name:"Mandible Hill", type:"Arena", cr:"8-21", mapPack:"2"},
              {name:"Fey Gate", type:"Arena", cr:"7-10", mapPack:"2"},
              {name:"Ice Castle", type:"Arena", cr:"14-21", mapPack:"2"},
              {name:"Golem Factory (Low)", type:"Arena", cr:"7-13", mapPack:"2"},
              {name:"Golem Factory (High)", type:"Arena", cr:"14-19", mapPack:"2"},
              {name:"Solar Cathedral", type:"Arena", cr:"7-21", mapPack:"2"},
              {name:"Depths of the Abyss", type:"Arena", cr:"7-24", mapPack:"2"},
              {name:"Titan's Grave", type:"Arena", cr:"21-22", mapPack:"2"},
              {name:"Death", type:"Arena", cr:"17-24", mapPack:"2"},
              {name:"Ultimate Depths", type:"Arena", cr:"17-22", mapPack:"2"},
              {name:"Heart of a Volcano", type:"Arena", cr:"12-21", mapPack:"2"},
              {name:"Dragon Lair (Forest)", type:"Arena", cr:"9-22", mapPack:"2"},
              {name:"Dragon Lair (Swamp)", type:"Arena", cr:"9-18", mapPack:"2"},
              {name:"Dragon Lair (Glacial)", type:"Arena", cr:"9-19", mapPack:"2"},
              {name:"Dragon Lair (Void)", type:"Arena", cr:"9,13,18", mapPack:"2"},
              {name:"Dragon Lair (Desert)", type:"Arena", cr:"9-19", mapPack:"2"},
              {name:"Dragon Lair (Deep Sea)", type:"Arena", cr:"9-21", mapPack:"2"},
              {name:"Chaos", type:"Boss Arena", cr:"30", mapPack:"2", link:"https://docs.google.com/document/d/1y8BJPXf7LfVvadQ2rMHIurUdkGmZxaC1cX9OnInA9gU/edit?usp=sharing"},
              {name:"Apocalypse", type:"Boss Arena", cr:"30", mapPack:"2", link:"https://docs.google.com/document/d/1GzrW38sOTMnGqTKankcXrEzSRjIa3lKMUklv-jib7Eo/edit?usp=sharing"},
            ],
            treasureByFloor:[
                ["3 Weapons, 3 Armor", "Enchanted Typed Weapon, Enchanted Typed Armor","Typed Perk, Stats", "R+ Perk, Gear", "Magic Perk, 2 Spells/Mnvers", "Martial Perk, 2 Spells/Mnvers", "4 Stats", "Weapon Enchantment, Armor Enchantment", "2 R+ Wonderous Items", "2 Typed Feats", "3 lvl+ Spells/Mnvrs", "2 Weapon Enchantments", "2 Armor Enchantments", "200 Gold", "Teamwork Feat", "5 Stats", "E+ Weapon Enchantment", "E+ Armor Enchantment"],
                ["3 Weapons, 3 Armor", "Enchanted Typed Weapon, Enchanted Typed Armor","Typed Perk, Stats", "R+ Perk, Gear", "Magic Perk, 2 Spells/Mnvers", "Martial Perk, 2 Spells/Mnvers", "4 Stats", "Weapon Enchantment, Armor Enchantment", "2 R+ Wonderous Items", "2 Typed Feats", "3 lvl+ Spells/Mnvrs", "2 Weapon Enchantments", "2 Armor Enchantments", "200 Gold", "Teamwork Feat", "5 Stats", "E+ Weapon Enchantment", "E+ Armor Enchantment"],
                ["L+ Weapons, L+ Armor", "R+ Typed Perk, Stats", "R+ Magic Perk, 3 Spells/Mnvers", "R+ Martial Perk, 3 Spells/Mnvers", "5 Stats", "R+ Weapon Enchantment, R+ Armor Enchantment", "2 E+ Wonderous Items", "3 Feats", "4 lvl+ Spells/Mnvrs", "2 R+ Weapon Enchantments", "2 R+ Armor Enchantments", "250 Gold", "Teamwork Feat, 3 Potions", "6 Stats", "L+ Weapon Enchantment", "L+ Armor Enchantment", "Level Up"],
                ["U Weapons, U Armor", "R+ Typed Perk, 2 Stats", "R+ Magic Perk, 4 Spells/Mnvers", "R+ Martial Perk, 4 Spells/Mnvers", "6 Stats", "E+ Weapon Enchantment, E+ Armor Enchantment", "L+ Typed Wonderous Item", "4 Wonderous Items", "3 Typed Feats", "4 lvl++ Spells/Mnvrs", "3 Weapon Enchantments", "2 E+ Weapon Enchantments", "3 Armor Enchantments", "2 E+ Armor Enchantments", "300 Gold", "2 Teamwork Feat", "8 Stats", "U Weapon Enchantment", "U Armor Enchantment", "Level Up, Typed Stats"],
                ["R+ Enchanted U Typed Weapons, R+ Enchanted U Typed Armor", "E+ Typed Perk, 3 Stats", "L+ Typed Perk", "E+ Magic Perk, 4 Spells/Mnvers", "E+ Martial Perk, 4 Spells/Mnvers", "8 Stats", "L+ Weapon Enchantment, L+ Armor Enchantment", "U Typed Wonderous Item, R+ weapon/armor enchantment", "4 R+ Wonderous Items", "4 Feats", "5 lvl++ Spells/Mnvrs", "3 R+ Weapon Enchantments", "2 E+ Weapon Enchantments, Wonderous Item", "3 R+ Armor Enchantments", "2 E+ Armor Enchantments, Wonderous Item", "350 Gold", "2 Teamwork Feat", "10 Stats", "U Weapon Enchantment, Perk", "U Armor Enchantment, Perk", "Level Up, 3 Stats"],
            ],
            treasureList:[
                {name:"Potion",worth:5},
                {name:"Weapon",worth:25},
                {name:"Armor",worth:25},
                {name:"E+ Typed Weapon",worth:100},
                {name:"E+ Typed Armor",worth:100},
                {name:"Enchanted Typed Weapon",worth:100},
                {name:"Enchanted Typed Armor",worth:100},
                {name:"Stats",worth:50},
                {name:"R+ Stats",worth:75},
                {name:"Wonderous Item",worth:75},
                {name:"R+ Wonderous Item",worth:100},
                {name:"E+ Wonderous Item",worth:125},
                {name:"Weapon Enchantment",worth:100},
                {name:"R+ Weapon Enchantment",worth:125},
                {name:"E+ Weapon Enchantment",worth:150},
                {name:"L+ Weapon Enchantment",worth:200},
                {name:"Armor Enchantment",worth:100},
                {name:"R+ Armor Enchantment",worth:125},
                {name:"E+ Armor Enchantment",worth:150},
                {name:"L+ Armor Enchantment",worth:200},
                {name:"Spell/Maneuver",worth:40},
                {name:"Typed Spell/Maneuver",worth:60},
                {name:"lvl+ Spell/Maneuver",worth:60},
                {name:"Typed lvl+ Spell/Maneuver",worth:80},
                {name:"Feat",worth:100},
                {name:"Typed Feat",worth:125},
                {name:"Perk",worth:125},
                {name:"Magic Perk",worth:125},
                {name:"Martial Perk",worth:125},
                {name:"Typed Perk",worth:150},
                {name:"R+ Perk",worth:150},
                {name:"E+ Perk",worth:200},
                {name:"L+ Perk",worth:250},
                {name:"Level Up",worth:250},
                
            ]
          }          
      },
      mounted(){
        let parsedMaps = this.mapList.map(map=>{
            map.CRs = []
            if(map.cr.includes("-")){
                let mapCRs = map.cr.split("-")
                for(let x = parseInt(mapCRs[0]); x <= parseInt(mapCRs[1]); x++){
                    map.CRs.push(x)
                }
            }else if(map.cr.includes(",")){
                let mapCRs = map.cr.split(",")
                for(let x = 0; x < mapCRs.length; x++){
                    map.CRs.push(parseInt(mapCRs[x]))
                }
            }else{
                map.CRs.push(parseInt(map.cr))
            }
            return map
        })
        this.mapList = parsedMaps
      },
      methods:{
        createJourneySelect(){
            this.mapsSelected = []
            let mapNames = []
            for(let x = this.floorCount-2; x >= 0; x--){
                if(x == 5) continue
                for(let y = 0; y < this.combatCount; y++){
                    let possibleMaps = this.mapList.filter(map=>{
                        if(this.floorCRs[x].some(item => map.CRs.includes(item))){
                            return true
                        }
                    })
                    let uniqueMaps = possibleMaps.filter(map => !mapNames.includes(map.name))
                    let selected = uniqueMaps[Math.floor(Math.random()*uniqueMaps.length)]
                    let matchingCRs = selected.CRs.filter(cr => this.floorCRs[x].includes(cr))
                    selected.specificCR = matchingCRs[Math.floor(Math.random()*matchingCRs.length)]
                    selected.rewards = this.getRewards(x, selected.specificCR)
                    mapNames.push(selected.name)
                    if(!this.mapsSelected[x]) this.mapsSelected[x] = []
                    this.mapsSelected[x].unshift(selected)
                }
            }
            if(this.floorCount > 1) this.generateStore(1, this.combatCount)
            if(this.floorCount > 3) this.generateStore(4, this.combatCount)
            //let bossFights = []
            let last = this.mapsSelected.length
            if(this.mapsSelected[last-1][0].specificCR){
                this.generateStore(last, 2)
            }else{
                this.mapsSelected[last-1] = {}
                this.generateStore(last, 2)
            }
            for(let x = 0; x < 2; x++){
                    let possibleMaps = this.mapList.filter(map=>{
                        if(map.CRs.includes(this.bossCR[this.floorCount-2])){
                            return true
                        }
                    })
                    let uniqueMaps = possibleMaps.filter(map => !mapNames.includes(map.name))
                    let selected = uniqueMaps[Math.floor(Math.random()*uniqueMaps.length)]
                    selected.specificCR = "Hidden (" + this.bossCR[this.floorCount-2] + ")"
                    selected.rewards = "Victory"
                    mapNames.push(selected.name)
                    if(!this.mapsSelected[last+1]) this.mapsSelected[last+1] = []
                    this.mapsSelected[last+1].push(selected)
                }

            //if(this.floorCount == 6) this.generateStore(7)
            this.journeySelect = this.mapsSelected
            this.journeySelect.splice(0,1,this.journeySelect[0])
        },

        generateStore(index, count){
            let stores = []
            for(let x = 0; x < count; x++){
                let possibleStores = this.storeTypes.filter(storeType => !stores.includes(storeType))
                stores.push(possibleStores[Math.floor(Math.random()*possibleStores.length)])
            }
            this.mapsSelected.splice(index,0,stores)
        },

        getRewards(floor, cr){
            let reward = this.treasureByFloor[floor][Math.floor(Math.random()*this.treasureByFloor[floor].length)]
            if(cr > this.averageCR[floor].slice(-1)) reward += ", 100 gold"
            else if(cr < this.averageCR[floor][0]) reward += ""
            else reward += ", 50 gold"
            return reward
        },

    //     getRewards(rewards, gold){
    //         let possibleRewards = this.treasureList.filter(treasure => treasure.worth <= gold)
    //         let reward = possibleRewards[Math.floor(Math.random()*possibleRewards.length)]
    //         //console.log(reward)
    //         gold -= reward.worth
    //         rewards.push(reward)
    //         possibleRewards = this.treasureList.filter(treasure => treasure.worth <= gold)
    //         if(possibleRewards.length > 1){
    //             rewards = this.getRewards(rewards, gold)
    //         }else{
    //             if(gold != 0){
    //                 if(Math.random() > 0.5){
    //                     rewards.push({name:Math.floor(gold/5) + " Potions"})
    //                 }else{
    //                     rewards.push({name:gold + " Gold"})
    //                 }
    //             }
    //             console.log("returning", rewards)
    //         }
    //         return rewards
    //     }
    },
}
</script>
<style>
table, th, td {
  border:1px solid black;
}
</style>
